import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Mail,
  Lock,
  AlertCircle,
  Loader,
  Eye,
  EyeOff,
  KeyRound,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Copy,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { login, verifyMfaOtp, resendMfaOtp } from "../../services/auth.service";
import { validateAllMessages, REGEX } from "../../utils/validation";
import { TOAST_MESSAGES, VALIDATION_MESSAGES } from "../../constants/messages";
 
const validationSchema = {
  email: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.email.required },
    { test: (v: string) => REGEX.email.test(v), message: VALIDATION_MESSAGES.email.invalid },
  ],
  password: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.password.required },
  ],
};
 
const Login = () => {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminLogin = location.pathname === "/admin/login";
 
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({ email: [], password: [] });
  const [touched, setTouched] = useState<Record<string, boolean>>({ email: false, password: false });
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
 
  // ── MFA state ──────────────────────────────────────────────
  const [mfaMode, setMfaMode] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expirySeconds, setExpirySeconds] = useState(0);
  const [devOtp, setDevOtp] = useState("");
  const otpInputRef = useRef<HTMLInputElement>(null);
 
  useEffect(() => {
    if (mfaMode) {
      otpInputRef.current?.focus();
    }
  }, [mfaMode]);
 
  useEffect(() => {
    if (mfaMode && devOtp && process.env.NODE_ENV !== "production") {
      console.log(
        "%c 🔐 MFA OTP Code ",
        "background: #2563eb; color: #ffffff; border-radius: 4px; font-size: 14px; font-weight: bold; padding: 4px 8px;",
        `Your verification code is: ${devOtp}`
      );
    }
  }, [mfaMode, devOtp]);
 
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);
 
  useEffect(() => {
    if (expirySeconds <= 0) return;
    const timer = setInterval(() => {
      setExpirySeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [expirySeconds]);
 
  const enterMfaMode = (token: string, expiry: number, devOtpCode?: string) => {
    setMfaToken(token);
    setOtp("");
    setOtpError("");
    setExpirySeconds(expiry);
    setResendCooldown(60);
    setApiError("");
    setDevOtp(devOtpCode || "");
    setMfaMode(true);
  };
 
  const exitMfaMode = () => {
    setMfaMode(false);
    setMfaToken("");
    setOtp("");
    setOtpError("");
    setExpirySeconds(0);
    setResendCooldown(0);
    setDevOtp("");
    setPassword("");
  };
 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");
 
    if (mfaMode) {
      await handleVerifyOtp();
      return;
    }
 
    const allErrors = validateAllMessages({ email, password }, validationSchema);
    setErrors(allErrors);
    setTouched({ email: true, password: true });
 
    const hasErrors = Object.values(allErrors).some((msgs) => msgs.length > 0);
    if (hasErrors) return;
 
    setIsLoading(true);
    try {
      const { hostname } = window.location;
      const subdomain =
        hostname.includes("diagnomiq.ai") && hostname.split(".")[0] !== "diagnomiq"
          ? hostname.split(".")[0]
          : undefined;
 
      const portal = isAdminLogin ? "admin" : "standard";
      const result = await login(email, password, subdomain, portal);
 
      if (result.requiresMfa && result.mfaToken) {
        enterMfaMode(result.mfaToken, result.mfa?.expirySeconds || 300, result.otp || result.mfa?.otp);
        toast.success("A verification code has been sent to your email.");
        setIsLoading(false);
        return;
      }
 
      if (result.token) {
        toast.success(TOAST_MESSAGES.success.login);
        loginUser(result.token, result.role, result.isSubscribed, result.user, result.tenant, result.mustChangePassword);
        return;
      } else {
        setApiError(TOAST_MESSAGES.error.loginInvalid);
        toast.error(TOAST_MESSAGES.error.loginInvalid);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || TOAST_MESSAGES.error.loginFailed;
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleVerifyOtp = async () => {
    const trimmed = otp.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setOtpError("Enter the 6-digit code from your email.");
      return;
    }
 
    setOtpError("");
    setIsLoading(true);
    try {
      const result = await verifyMfaOtp(mfaToken, trimmed);
      if (result.token) {
        toast.success(TOAST_MESSAGES.success.login);
        setMfaMode(false);
        loginUser(result.token, result.role, result.isSubscribed, result.user, result.tenant);
        return;
      }
      const msg = result.message || "Invalid verification code.";
      setApiError(msg);
      toast.error(msg);
      setOtp("");
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Verification failed. Please try again.";
      setApiError(msg);
      toast.error(msg);
      setOtp("");
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setOtpError("");
    setApiError("");
    setIsLoading(true);
    try {
      const result = await resendMfaOtp(mfaToken);
      if (result.success) {
        setOtp("");
        setExpirySeconds(result.mfa?.expirySeconds || 300);
        setResendCooldown(60);
        if (result.otp || result.mfa?.otp) setDevOtp(result.otp || result.mfa?.otp);
        toast.success("A new verification code has been sent.");
      } else {
        const msg = result.message || "Failed to resend the verification code.";
        setApiError(msg);
        toast.error(msg);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to resend the verification code.";
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };
 
  const validateField = (field: string, value: string): string[] => {
    const rules = validationSchema[field as keyof typeof validationSchema];
    if (!rules) return [];
    const fieldErrors: string[] = [];
    for (const rule of rules) {
      if (!rule.test(value)) {
        fieldErrors.push(rule.message);
      }
    }
    return fieldErrors;
  };
 
  const handleChange = (field: string, value: string) => {
    if (field === "email") setEmail(value);
    else setPassword(value);
 
    setApiError("");
 
    if (touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    }
  };
 
  const handleBlur = (field: string) => {
    const value = field === "email" ? email : password;
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };
 
  return (
    <div className="relative w-screen min-h-screen md:h-screen flex flex-col md:flex-row overflow-x-hidden md:overflow-hidden bg-white">
      {/* Asymmetric Curved Divider SVG (Visible on desktop/tablet) */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-[1] hidden md:block" aria-hidden="true">
        <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          <defs>
            <linearGradient id="portalTealGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#26ABB0" />
              <stop offset="100%" stopColor="#219FA4" />
            </linearGradient>
          </defs>
          <path
            d="M 526.3 0 C 464.5 333.3, 475.4 666.7, 602.3 1000 L 1000 1000 L 1000 0 Z"
            fill="url(#portalTealGrad)"
          />
        </svg>
      </div>

      {/* LEFT PANEL: Branding & Decorative Healthcare Graphics */}
      <div className="relative w-full md:w-1/2 h-auto md:h-full flex flex-col justify-between px-6 py-4 sm:p-11 lg:p-14 z-[2] bg-white md:bg-transparent shrink-0">
        {/* Center Branding using original SVG logo */}
        <div className="my-3 sm:my-4 md:my-0 md:absolute md:top-[43%] md:left-[50%] md:-translate-x-1/2 md:-translate-y-1/2 flex flex-col items-center justify-center text-center w-full max-w-[90%] md:max-w-[720px] lg:max-w-[820px] xl:max-w-[880px] px-2">
          <img
            src="/logo/DiagnomIQ-01.svg"
            alt="DiagnomIQ - Precision insights for every patient"
            className="w-full max-w-[340px] sm:max-w-[480px] md:max-w-[680px] lg:max-w-[780px] xl:max-w-[840px] h-auto object-contain"
          />
        </div>

        {/* Decorative Medical Graphic in Bottom-Left */}
        <svg
          className="hidden md:block absolute bottom-0 left-0 w-auto h-[48vh] max-h-[500px] pointer-events-none z-[1]"
          viewBox="0 0 380 500"
          fill="none"
          aria-hidden="true"
        >
          {/* Outer Stroke */}
          <path
            d="M 90 -20 L 126 15 Q 140 29 154 43 L 265 154 Q 283 172 283 198 L 283 310 Q 283 336 265 354 L 154 465 Q 140 479 126 493 L 90 528"
            stroke="#D2EEF1"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          {/* Pale Shape Fill */}
          <path
            d="M 0 57 L 61 57 Q 78 57 90 69 L 204 183 Q 222 201 222 225 L 222 297 Q 222 321 204 339 L 90 453 Q 78 465 61 465 L 0 465 Z"
            fill="#EDFAFB"
          />
          {/* White Cross */}
          <path
            d="M 0 133 L 46 133 Q 67 133 67 154 L 67 216 Q 67 234 85 234 L 138 234 Q 158 234 158 254 L 158 284 Q 158 304 138 304 L 85 304 Q 67 304 67 322 L 67 384 Q 67 405 46 405 L 0 405 Z"
            fill="#FFFFFF"
          />
        </svg>
      </div>

      {/* RIGHT PANEL: Teal Background & Login Form */}
      <div className="relative w-full md:w-1/2 flex-1 md:h-full flex items-center justify-center p-6 sm:p-10 lg:p-14 z-[2] bg-[#23A4A8] md:bg-transparent rounded-t-[32px] md:rounded-none shadow-lg md:shadow-none min-h-[520px]">
        {/* Top-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute top-0 right-0 w-[320px] lg:w-[450px] h-[320px] lg:h-[450px] pointer-events-none z-[1]"
          viewBox="0 0 450 450"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="492" cy="-25" r="225" fill="#2FAFB1" />
          <circle cx="492" cy="-25" r="162" fill="#48B9BC" />
        </svg>

        {/* Bottom-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute bottom-0 right-0 w-[380px] lg:w-[550px] h-[380px] lg:h-[550px] pointer-events-none z-[1]"
          viewBox="0 0 550 550"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="578" cy="651" r="298" fill="#33AAAD" />
          <circle cx="578" cy="651" r="244" fill="#44B6B9" />
        </svg>

        {/* Form Container Sitting Directly on Teal Background */}
        <div className="relative z-[3] w-full max-w-[465px]">
          {mfaMode ? (
            <div className="space-y-5">
              <div className="space-y-2">
                <div className="flex justify-center mb-3">
                  <div className="inline-flex p-3.5 sm:p-4 rounded-full bg-white/20 text-white">
                    <ShieldCheck className="h-8 w-8 sm:h-10 sm:w-10" />
                  </div>
                </div>
                <h1 className="text-[26px] sm:text-[30px] font-bold text-white tracking-tight leading-tight">
                  Two-Factor Verification
                </h1>
                <p className="text-[14.5px] sm:text-[15.5px] text-[#E0F7F6] leading-relaxed">
                  A 6-digit verification code was sent to{" "}
                  <span className="font-semibold text-white">{email}</span>.
                  Enter it below to complete signing in.
                </p>
                {expirySeconds > 0 && (
                  <p className="text-[12.5px] sm:text-[13px] text-[#E0F7F6]/90 font-medium">
                    Code expires in <span className="font-semibold text-white">{expirySeconds}s</span>
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                <div>
                  <label htmlFor="otp" className="block text-[14.5px] sm:text-[15px] font-semibold text-white mb-2">
                    Verification Code
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-[#94A3B8]" />
                    </div>
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData("text");
                        const digits = pasted.replace(/\D/g, "").slice(0, 6);
                        if (digits) {
                          setOtp(digits);
                        }
                      }}
                      ref={otpInputRef}
                      className="w-full h-[54px] sm:h-[56px] bg-white rounded-md pl-12 pr-4 text-center text-lg font-mono tracking-[0.3em] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm focus:ring-2 focus:ring-[#097173]"
                      placeholder="••••••"
                    />
                  </div>
                  {otpError && <p className="mt-1.5 text-xs sm:text-sm text-red-100 font-medium">{otpError}</p>}
                </div>

                {apiError && (
                  <div className="flex items-center space-x-2 text-white bg-red-600/40 border border-red-300/40 p-3 rounded-md text-sm">
                    <AlertCircle className="h-5 w-5 shrink-0 text-red-200" />
                    <span>{apiError}</span>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-[54px] sm:h-[56px] bg-[#097173] hover:bg-[#075E60] active:scale-[0.99] text-white font-semibold text-[16px] rounded-md flex items-center justify-center space-x-2 shadow-lg transition-all duration-150 disabled:opacity-60 cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="h-5 w-5 mr-2 animate-spin" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <span>Verify &amp; Sign In</span>
                        <ArrowRight className="h-5 w-5 ml-1" />
                      </>
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || isLoading}
                    className="text-[14px] font-medium text-white hover:text-[#D8F2F1] underline disabled:opacity-50 disabled:no-underline cursor-pointer transition-colors"
                  >
                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
                  </button>

                  <button
                    type="button"
                    onClick={exitMfaMode}
                    className="inline-flex items-center text-[14px] font-medium text-white/90 hover:text-white cursor-pointer transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to login
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* ── Normal Login Form (Exact Visual Match to Reference) ───────── */
            <>
              <h1 className="text-[30px] sm:text-[34px] font-bold text-white tracking-tight leading-tight mb-2">
                Welcome Back
              </h1>
              <p className="text-[14.5px] sm:text-[15.5px] font-normal text-[#E0F7F6] mb-7 leading-relaxed">
                {isAdminLogin
                  ? "Sign in with your administrator credentials"
                  : "Sign in to access your personalized precision care treatment system"}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-[14.5px] sm:text-[15px] font-semibold text-white mb-1.5">
                    Email
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-[#94A3B8] pointer-events-none flex items-center" aria-hidden="true">
                      <Mail className="h-5 w-5" />
                    </span>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      onBlur={() => handleBlur("email")}
                      placeholder="Enter your email address"
                      className="w-full h-[54px] sm:h-[56px] bg-white rounded-md pl-12 pr-4 text-[15px] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm transition-all focus:ring-2 focus:ring-[#097173] border border-white/40"
                    />
                  </div>
                  {touched.email && errors.email?.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {errors.email.map((msg, i) => (
                        <p key={i} className="text-xs text-red-100 font-medium flex items-center gap-1">
                          <AlertCircle className="h-3 w-3 flex-shrink-0 text-red-200" />
                          {msg}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-[14.5px] sm:text-[15px] font-semibold text-white mb-1.5">
                    Password
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-[#94A3B8] pointer-events-none flex items-center" aria-hidden="true">
                      <Lock className="h-5 w-5" />
                    </span>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      onBlur={() => handleBlur("password")}
                      placeholder="Enter your password"
                      className="w-full h-[54px] sm:h-[56px] bg-white rounded-md pl-12 pr-12 text-[15px] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm transition-all focus:ring-2 focus:ring-[#097173] border border-white/40"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 text-[#94A3B8] hover:text-[#64748B] focus:outline-none p-1 transition-colors cursor-pointer"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {touched.password && errors.password?.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {errors.password.map((msg, i) => (
                        <p key={i} className="text-xs text-red-100 font-medium flex items-center gap-1">
                          <AlertCircle className="h-3 w-3 flex-shrink-0 text-red-200" />
                          {msg}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                {/* Forgot Password Link (Aligned Right) */}
                <div className="flex justify-end pt-0.5">
                  <Link
                    to="/forgot-password"
                    className="text-white text-[14px] sm:text-[14.5px] font-medium hover:text-[#E0F7F6] hover:underline transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Error Banner */}
                {apiError && (
                  <div className="flex items-center space-x-2 text-white bg-red-600/40 border border-red-300/40 p-3 rounded-md text-sm">
                    <AlertCircle className="h-5 w-5 shrink-0 text-red-200" />
                    <span>{apiError}</span>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-1.5">
                  <button
                    type="submit"
                    disabled={isLoading}
                    aria-label="Sign in"
                    className="w-full h-[54px] sm:h-[56px] bg-[#097173] hover:bg-[#075E60] active:scale-[0.99] text-white font-semibold text-[16px] sm:text-[16.5px] rounded-md flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="h-5 w-5 mr-2 animate-spin" />
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <>
                        <span>{isAdminLogin ? "Sign In as Admin" : "Sign In"}</span>
                        <ArrowRight className="h-5 w-5 ml-1" />
                      </>
                    )}
                  </button>
                </div>

                {/* Footer links: Register and Legal documents (Normal login only) */}
                {!isAdminLogin && (
                  <div className="text-center space-y-3.5 pt-3.5">
                    <p className="text-[15px] sm:text-[15.5px] font-medium text-white/95">
                      Don't have an account?{' '}
                      <Link
                        to="/register"
                        className="font-bold text-white underline hover:text-[#D8F2F1] transition-colors ml-1"
                      >
                        Register
                      </Link>
                    </p>

                    <div className="pt-2.5 border-t border-white/25 flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1 text-[13px] sm:text-[13.5px] font-medium text-white/85">
                      <Link
                        to="/legal?tab=terms-and-conditions"
                        className="hover:text-white hover:underline transition-colors"
                      >
                        Terms
                      </Link>
                      <span className="text-white/60 text-xs select-none">&bull;</span>
                      <Link
                        to="/legal?tab=privacy-policy"
                        className="hover:text-white hover:underline transition-colors"
                      >
                        Privacy
                      </Link>
                      <span className="text-white/60 text-xs select-none">&bull;</span>
                      <Link
                        to="/legal?tab=medical-disclaimer"
                        className="hover:text-white hover:underline transition-colors"
                      >
                        Medical Disclaimer
                      </Link>
                    </div>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>

      {/* Dev/QA OTP badge — only visible in non-production */}
      {mfaMode && devOtp && process.env.NODE_ENV !== "production" && (
        <div
          className="fixed bottom-4 right-4 z-50 bg-neutral-900/95 text-white px-4 py-2.5 rounded-lg shadow-2xl text-sm font-mono backdrop-blur-md flex items-center space-x-3 border border-neutral-700 select-text"
          style={{ userSelect: "text", WebkitUserSelect: "text" }}
        >
          <span className="opacity-70 select-text" style={{ userSelect: "text", WebkitUserSelect: "text" }}>
            Dev OTP:
          </span>
          <span
            className="font-bold tracking-widest text-emerald-400 select-text cursor-text"
            style={{ userSelect: "text", WebkitUserSelect: "text" }}
          >
            {devOtp}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(devOtp);
                toast.success("Dev OTP copied to clipboard");
              }
            }}
            className="p-1.5 hover:bg-neutral-800 rounded-md transition-colors text-white/80 hover:text-white cursor-pointer"
            title="Copy Dev OTP to clipboard"
            aria-label="Copy Dev OTP to clipboard"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};
 
export default Login;